import React, { useEffect } from 'react';
import { MapStateToProps, MapDispatchToPropsFunction, connect } from "react-redux";
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core'
import { IStoreState } from '../Reducers/rootReducer'
import ModalHeader from "./ModalHeader";
import {closeNewsDetailsModal, ICloseNewsDetailsModal} from "shared/Modules/News/newsActions";
import {INews} from "shared/Types/appTypes";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLink} from "@fortawesome/pro-solid-svg-icons/faLink";
import {faPrint} from "@fortawesome/pro-solid-svg-icons/faPrint";
import {faClock} from "@fortawesome/pro-regular-svg-icons/faClock";
import ShareHelper from "shared/Helpers/ShareHelper";
import { Localized } from '@fluent/react';
import { DetailDrawer } from 'shared/Components/Dialog/Drawers';
import { useAppErrorHandling } from 'shared/Modules/Error/errorHooks';
import { DynamicHtml } from 'shared/Components/Dynamic/DynamicHtml';

// These are the props that can/should be passed from a parent component
// It is the only exported prop interface since all other props are internal
export interface IOwnProps {
    // This component does not have own props
}

// These are the props we expect to be passed from Redux state
interface IStateProps {
    isDrawerOpen: boolean
    news: INews | null
}

// These are the props used to dispatch actions from the component
interface IDispatchProps {
    hideNewsDetailsModal: () => ICloseNewsDetailsModal
}

type IProps =
    & IOwnProps
    & IStateProps
    & IDispatchProps

const mapStateToProps: MapStateToProps<IStateProps, IOwnProps, IStoreState> = ({ newsDetailsModal }) => ({
    isDrawerOpen: newsDetailsModal.open,
    news: newsDetailsModal.news,
});
const mapDispatchToProps: MapDispatchToPropsFunction<IDispatchProps, IOwnProps> = (dispatch) => ({
    hideNewsDetailsModal: () => dispatch(closeNewsDetailsModal()),
});

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            minHeight: '100%',
            width: '100vw',
            overflow: 'auto',
            paddingBottom: 50,
        },
        header: {
            paddingTop: 10,
            paddingLeft: 20,
            paddingRight: 20,
        },
        content: {
            //paddingTop: 60,
        },
        imageDiv: {
            position: "relative",
            height: 200,
        },
        image: {
            objectFit: "cover",
            height: "100%",
            width: "100%",
        },
        container: {
            padding: 28,
        },
        subject: {
            fontWeight: "bolder",
            marginTop: 0,
            marginBottom: 0,
        },
        lastUpdated: {
            color: '#969696',
            fontSize: "0.9375rem",  // = 15px @ std
            letterSpacing: -0.25,
            lineHeight: '16px',
            marginTop: 12,
            marginBottom: 0,
        },
        body: {
            marginTop: 30,
            marginBottom: 0,
        },
        headerIcon: {
            fontSize: theme.typography.h6.fontSize,
            color: '#505050',
            margin: '10px 9px',
        },
    }),
);

const ModalContent = (props: IProps & { classes: ReturnType<typeof useStyles> }) => {
    const { hideNewsDetailsModal, news } = props;
    const classes = useStyles();
    const { dispatchInternalError } = useAppErrorHandling()

    useEffect(() => {
        if (news === null) {
            dispatchInternalError("Missing news item")
            hideNewsDetailsModal()
        }
    })

    if (news === null) {
        return (
            <div />
        );
    }

    const details = () => {
        return (
            <div role="presentation" className={classes.content}>
                {news.imageUrl && (
                    <div role="presentation" className={classes.imageDiv}>
                        <img
                            src={news.imageUrl}
                            alt={news.subject}
                            title={news.subject}
                            className={classes.image}
                        />
                    </div>
                )}

                <div role="presentation" className={classes.container}>
                    <Typography variant="h5" className={classes.subject}>{news.subject}</Typography>

                    <p className={classes.lastUpdated}>
                        <span style={{marginRight:15}}>
                            <FontAwesomeIcon icon={faClock} style={{marginRight:5}} />
                            {news.displayPublishDate}
                        </span>
                    </p>

                    <DynamicHtml html={news.body} className={classes.body} />
                </div>
            </div>
        );
    }

    const shareEvent = ShareHelper.shareLink(
        news.permaLink,
        news.subject,
        news.shortDescription,
    );

    return (
        <div role="presentation" className={classes.root}>
            <div role="presentation" className={classes.header}>
                <Localized id="newsDetails-header-label" attrs={{title: true}}>
                    <ModalHeader fixed={true} title="My News" onClose={hideNewsDetailsModal} rightColumnContent={
                        <div>
                            <FontAwesomeIcon className={classes.headerIcon} icon={faLink} onClick={shareEvent} />
                            <FontAwesomeIcon className={classes.headerIcon} icon={faPrint} onClick={() => {
                                // @ts-ignore
                                if (typeof cordova !== 'undefined' && cordova.plugins.printer && cordova.plugins.printer.print) {
                                    // @ts-ignore
                                    cordova.plugins.printer.print();
                                } else if (window.print) {
                                    window.print();
                                } else {
                                    alert('print API not supported!');
                                }
                            }} />
                        </div>
                    } />
                </Localized>
            </div>

            {details()}
        </div>
    );
}

const Modal = (props: IProps) => {
    const { isDrawerOpen, hideNewsDetailsModal } = props;
    const classes = useStyles();

    return (
        <DetailDrawer open={isDrawerOpen} onClose={hideNewsDetailsModal}>
            <ModalContent {...props} classes={classes} />
        </DetailDrawer>
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(Modal);
